// BannerManager.jsx — SAME UI as CarsManager (grid + add card + custom modal + dropzone + previews)
// ✅ Single image upload (only 1 image can be selected/kept)
// ✅ title_banner is OPTIONAL (nu e obligatoriu)

import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";
import "../../styles/admin/CarManager.css";
import "../../styles/admin/CarAdd.css";

import { useDropzone } from "react-dropzone";

function BannerManager() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [title_banner, setTitleBanner] = useState("");
  const [uploaded_images, setUploadedImages] = useState([]); // File[] (max 1)
  const [previews, setPreviews] = useState([]); // [{file, url}] (max 1)

  /* =========================
     HELPERS
  ========================== */
  const parseApiError = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "A apărut o eroare.";

    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (data.detail) return data.detail;

    const k = Object.keys(data)[0];
    const v = data[k];
    if (Array.isArray(v)) return v[0];
    if (typeof v === "string") return v;

    return "A apărut o eroare.";
  };

  const normalizeList = (data) => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  };

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /* =========================
     FETCH
  ========================== */
  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/produs/banners");
      setBanners(normalizeList(res.data));
    } catch (e) {
      setError(parseApiError(e));
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  /* =========================
     AUTO-DISMISS MESSAGE (5s)
  ========================== */
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(""), 5000);
    return () => clearTimeout(t);
  }, [message]);

  /* =========================
     MODAL
  ========================== */
  const openModal = () => {
    setError("");
    setMessage("");
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    document.body.style.overflow = "";
    setTitleBanner("");
    setUploadedImages([]);
    setError("");
    setMessage("");
  };

  // close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showModal) closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, submitting]);

  /* =========================
     DROPZONE (SINGLE IMAGE)
  ========================== */
  const onDrop = (acceptedFiles, rejections) => {
    setError("");

    if (rejections?.length) {
      const msg =
        rejections?.[0]?.errors?.[0]?.message ||
        "Unele fișiere au fost respinse (format).";
      setError(msg);
    }

    if (!acceptedFiles?.length) return;

    // ✅ keep only ONE: replace existing with the newest selected
    const file = acceptedFiles[0];
    setUploadedImages([file]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
    maxFiles: 1,
    disabled: submitting,
  });

  useEffect(() => {
    // cleanup previous previews
    previews.forEach((p) => URL.revokeObjectURL(p.url));

    const file = uploaded_images?.[0];
    const next = file ? [{ file, url: URL.createObjectURL(file) }] : [];
    setPreviews(next);

    return () => next.forEach((p) => URL.revokeObjectURL(p.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploaded_images]);

  const removeImage = () => {
    setUploadedImages([]);
  };

  /* =========================
     ACTIONS
  ========================== */
  const createBanner = async (e) => {
    e?.preventDefault?.();
    setError("");

    // ✅ title_banner NU mai e obligatoriu
    if (!uploaded_images?.length) {
      setError("Adaugă o imagine.");
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();

      // ✅ trimitem titlul doar dacă există (altfel backend-ul nu primește câmp gol)
      const title = title_banner.trim();
      if (title) formData.append("title_banner", title);

      // ✅ single image only
      formData.append("image_banner", uploaded_images[0]);

      await api.post("/produs/banners", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage("Banner adăugat cu succes.");
      await fetchBanners();
      closeModal();
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteBanner = async (bannerId, e) => {
    e?.stopPropagation?.();
    setError("");

    const ok = window.confirm("Sigur vrei să ștergi acest banner?");
    if (!ok) return;

    try {
      await api.delete(`/produs/banners/${bannerId}`);
      setMessage("Banner șters.");
      await fetchBanners();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  /* =========================
     SORT (newest first)
  ========================== */
  const bannersSorted = useMemo(() => {
    return [...banners].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0));
  }, [banners]);

  if (loading) return <div className="srCar_error">Loading...</div>;

  return (
    <div className="srCar_wrap">
      {/* messages outside modal */}
      {message && !showModal && (
        <div
          className="srCar_error"
          style={{
            background: "rgba(0,200,120,.18)",
            borderColor: "rgba(0,200,120,.35)",
          }}
        >
          {message}
        </div>
      )}
      {error && !showModal && <div className="srCar_error">{error}</div>}

      <div className="srCar_grid">
        {/* ADD CARD */}
        <div className="srCar_addCard" onClick={openModal}>
          <div className="srCar_addMedia">
            <div className="srCar_addIcon">
              <FontAwesomeIcon icon={faPlus} size="1x" />
            </div>
          </div>
          <div className="srCar_addLabel">Add Banner</div>
        </div>

        {/* LIST */}
        {bannersSorted.map((b, idx) => {
          const img = b.image_banner || b.banner || b.image || b.url;
          const title =
            (b.title_banner && String(b.title_banner).trim()) ||
            (b.name && String(b.name).trim()) ||
            (b.title && String(b.title).trim()) ||
            `Banner #${idx + 1}`; // ✅ fallback dacă nu există titlu

          return (
            <div key={b.id ?? `banner-${idx}`} className="srCar_card">
              <div className="srCar_cardMedia">
                {img ? <img className="srCar_thumb" src={img} alt={title} /> : null}

                <button
                  type="button"
                  className="srCar_deleteBtn"
                  onClick={(e) => deleteBanner(b.id, e)}
                  title="Delete"
                  aria-label="Delete banner"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="srCar_title">{title}</div>
            </div>
          );
        })}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="srCarModal_overlay" onMouseDown={closeModal}>
          <div
            className="srCarModal_content"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* messages inside modal */}
            {message && (
              <div
                className="srCar_error"
                style={{
                  background: "rgba(0,200,120,.18)",
                  borderColor: "rgba(0,200,120,.35)",
                }}
              >
                {message}
              </div>
            )}
            {error && <div className="srCar_error">{error}</div>}

            <div className="srCarModal_header">
              <h3 className="srCarModal_heading">Add Banner</h3>

              <button
                type="button"
                className="srCarModal_closeBtn"
                onClick={closeModal}
                disabled={submitting}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="srCarModal_body">
              <form className="srCarF_form" onSubmit={createBanner}>
                <label>
                  <input
                    type="text"
                    value={title_banner}
                    placeholder="Banner Name (optional)"
                    onChange={(e) => setTitleBanner(e.target.value)}
                    disabled={submitting}
                  />
                </label>

                <div
                  {...getRootProps()}
                  className={`srCar_dropzone srCarF_full ${isDragActive ? "is-drag" : ""}`}
                  style={{ opacity: submitting ? 0.7 : 1 }}
                >
                  <input {...getInputProps()} />
                  <p>
                    Drag & drop o imagine sau click{" "}
                    {uploaded_images?.length ? "(se va înlocui imaginea curentă)" : ""}
                  </p>

                  {previews.length > 0 && (
                    <div className="srCar_previews">
                      {previews.map((p) => (
                        <div
                          key={p.url}
                          className="srCar_previewItem"
                          onClick={stop}
                          onMouseDown={stop}
                          onPointerDown={stop}
                        >
                          <img className="srCar_previewImg" src={p.url} alt={p.file.name} />

                          <button
                            type="button"
                            className="srCarF_btn srCarF_btnDanger"
                            onMouseDown={stop}
                            onPointerDown={stop}
                            onClick={(e) => {
                              stop(e);
                              removeImage();
                            }}
                            disabled={submitting}
                          >
                            Șterge
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="srCarF_actions">
                  <button
                    className="srCarF_btn srCarF_btnPrimary"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? "Saving..." : "Add Banner"}
                  </button>

                  <button
                    className="srCarF_btn srCarF_btnDanger"
                    type="button"
                    onClick={closeModal}
                    disabled={submitting}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BannerManager;